import { useMemo } from "react";
import { relativeTime } from "@playbookai/ui";
import { effectiveTimeZone } from "./presentation-replacement.js";
import { usePresentationPreferencesQuery } from "./useSettings.js";

/**
 * The ONE way the shell renders an absolute date or time (PB-5134, d19/d27).
 *
 * Every formatter takes the zone explicitly so pure callers and specs stay
 * deterministic; `useDateTimeFormatter` binds them to the zone the user
 * effectively has — the stored one once the presentation row is persisted
 * (version >= 1, which d29 guarantees carries a real zone), the detected
 * browser zone until then. Relative copy ("3d ago") is zone-free, but its
 * over-seven-day fallback is a date, and it goes through the same zone so a
 * page never shows two.
 */
export type DateTimeInput = string | number | Date;

const MAX_DATE_MS = 8.64e15;

function toMs(value: DateTimeInput): number | null {
  const ms =
    typeof value === "number"
      ? value
      : value instanceof Date
        ? value.getTime()
        : new Date(value).getTime();
  return Number.isFinite(ms) && Math.abs(ms) <= MAX_DATE_MS ? ms : null;
}

function formatter(
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone });
  } catch {
    // An unknown zone (a stored value this runtime's ICU lacks) must not
    // take the page down; the host zone is the honest fallback.
    return new Intl.DateTimeFormat(undefined, options);
  }
}

/**
 * The locale for parts that are READ BACK rather than shown.
 *
 * `formatter` follows the reader's locale, which is right for display and
 * wrong for a key. `formatToParts` fixes the ORDER of the parts, never their
 * VALUES, so the reader's locale still chooses the calendar and the digits:
 * under `ar-EG` the year comes back "٢٠٢٦", which fails every `\d` here
 * (JS `\d` is ASCII-only), and under `th-TH` the Buddhist "2569", which
 * PASSES and is silently 543 years out. Both reach the audit filter — the
 * first as a NaN day boundary, the second as a real instant in the wrong
 * century.
 *
 * Pinning Gregorian and Latin digits is what actually makes a machine-read
 * part the same in every locale, which is what the callers below have always
 * claimed to be. Display formatting is untouched and stays localized.
 */
const MACHINE_PARTS_LOCALE = "en-US-u-ca-gregory-nu-latn";

function machineParts(
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(MACHINE_PARTS_LOCALE, {
      ...options,
      timeZone,
    });
  } catch {
    // Same reason as `formatter`: an unknown zone must not take the page down.
    return new Intl.DateTimeFormat(MACHINE_PARTS_LOCALE, options);
  }
}

/** e.g. "Aug 29, 2026, 10:00 AM" in `timeZone`; "" for an unusable input. */
export function formatDateTime(value: DateTimeInput, timeZone: string): string {
  const ms = toMs(value);
  if (ms === null) return "";
  return formatter(timeZone, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(ms);
}

/**
 * `formatDateTime`, with the caller's own word for a value that is not there.
 *
 * The absent text is the caller's because it is not decoration: "—" reads
 * correctly in a definition list, and "Never" reads correctly for a
 * last-checked time where the absence IS the fact. An UNUSABLE value gets the
 * same word — `formatDateTime` answers "" there, and an empty cell is
 * indistinguishable from one nobody rendered.
 *
 * This replaces six copies of `formatMoment`, which each formatted in the
 * browser's zone rather than the reader's (PB-5221).
 */
export function formatDateTimeOr(
  value: DateTimeInput | null | undefined,
  timeZone: string,
  absent: string,
): string {
  if (value === null || value === undefined) return absent;
  return formatDateTime(value, timeZone) || absent;
}

/**
 * A COMPACT instant — "29 Aug, 16:29" — in `timeZone`; "" for an unusable input.
 *
 * The year and the seconds are dropped, for the one place a full medium
 * timestamp wrapped to four lines: an overlap line that names two instants in
 * a sentence. Same zone rule as every other formatter here.
 */
export function formatDateTimeShort(
  value: DateTimeInput,
  timeZone: string,
): string {
  const ms = toMs(value);
  if (ms === null) return "";
  return formatter(timeZone, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(ms);
}

/** e.g. "Aug 29, 2026" in `timeZone`; "" for an unusable input. */
export function formatDate(value: DateTimeInput, timeZone: string): string {
  const ms = toMs(value);
  if (ms === null) return "";
  return formatter(timeZone, { dateStyle: "medium" }).format(ms);
}

/** The clock time with seconds, e.g. "10:00:07 AM", in `timeZone`; "" for an
 * unusable input. For a column whose day is named once above it (the Audit
 * record's day groups, PB-5216 d7), where repeating the date per row is noise
 * and the seconds are the part that orders events. */
export function formatTime(value: DateTimeInput, timeZone: string): string {
  const ms = toMs(value);
  if (ms === null) return "";
  return formatter(timeZone, { timeStyle: "medium" }).format(ms);
}

const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The calendar date at `value`, in `timeZone`, as `YYYY-MM-DD`.
 *
 * Assembled from `formatToParts` under `machineParts`, so the result is the
 * same in every locale. Parts alone are not enough for that — they fix the
 * order and leave the calendar and the digits to the reader's locale — which
 * is why the locale is pinned rather than left to default. This is the
 * KEY form of a date — for grouping, comparing and range arithmetic — while
 * `formatDate` is the readable form. Host `getDate()` is never a substitute:
 * it answers in the host's zone while the page renders in the reader's.
 * "" for an unusable input. */
export function isoDateInZone(value: DateTimeInput, timeZone: string): string {
  const ms = toMs(value);
  if (ms === null) return "";
  const parts = machineParts(timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(ms);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  const [year, month, day] = [get("year"), get("month"), get("day")];
  return year && month && day ? `${year}-${month}-${day}` : "";
}

/** How far `timeZone` is ahead of UTC at `ms`, in milliseconds.
 *
 * `machineParts` for the same reason `isoDateInZone` uses it, with a sharper
 * edge: these parts go through `Number`, so a locale's own digits do not
 * merely fail a regex, they produce `NaN` and carry it into every day
 * boundary built on this. */
function zoneOffset(ms: number, timeZone: string): number {
  const parts = machineParts(timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(ms);
  const get = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  const wall = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  // `ms` may carry sub-second precision the parts cannot; drop it from both
  // sides so the difference is the offset and nothing else.
  return wall - Math.floor(ms / 1000) * 1000;
}

/**
 * The instant at which the calendar day `YYYY-MM-DD` begins in `timeZone`.
 *
 * `new Date(year, month - 1, day)` answers in the HOST's zone, which is a
 * different day boundary from the one the page renders in as soon as the
 * reader has chosen a Region. The offset is applied twice because it is
 * itself a function of the instant: the first pass lands within a few hours
 * of the target, and the second corrects it when a DST transition sits
 * between the guess and the answer.
 *
 * `null` for a malformed day, and for one that does not exist (2026-02-30,
 * which plain `Date` rolls silently forward into March).
 */
export function startOfDayInZone(day: string, timeZone: string): number | null {
  const match = ISO_DAY_RE.exec(day);
  if (!match) return null;
  const [year, month, date] = [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
  ];
  const wall = Date.UTC(year, month - 1, date);
  // Reject a rolled-forward day before doing any zone arithmetic.
  const rolled = new Date(wall);
  if (
    rolled.getUTCFullYear() !== year ||
    rolled.getUTCMonth() !== month - 1 ||
    rolled.getUTCDate() !== date
  ) {
    return null;
  }
  const first = wall - zoneOffset(wall, timeZone);
  return wall - zoneOffset(first, timeZone);
}

/** The calendar day `count` days before `YYYY-MM-DD`, as `YYYY-MM-DD`; "" if
 * malformed.
 *
 * Pure calendar arithmetic on the parts, so it is unaffected by any zone and
 * by every DST transition — the day before the 29th is the 28th whether that
 * day was 23, 24 or 25 hours long, and 90 days before a date is 90 calendar
 * days whatever clocks did in between. `Date.UTC` normalises an out-of-range
 * day, so the shift rolls back through months and years on its own.
 *
 * Generalised from `previousDay` (which is this at `count = 1`) when the audit
 * admin needed a 90-day bound: that page had grown its own copy of this
 * arithmetic AND a second copy of the ISO-day regex. One parse and one
 * explanation is the point — the regex is the half that actually costs
 * something when it drifts. */
export function daysBefore(day: string, count: number): string {
  const match = ISO_DAY_RE.exec(day);
  if (!match) return "";
  const shifted = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) - count),
  );
  return shifted.toISOString().slice(0, 10);
}

/** The calendar day before `YYYY-MM-DD`, as `YYYY-MM-DD`; "" if malformed. */
export function previousDay(day: string): string {
  return daysBefore(day, 1);
}

/** The zone's short name as Intl renders it for this locale — "UTC", "BST",
 * "GMT+5:30" — for naming the zone once in a header. Falls back to the IANA
 * identifier when Intl has no short form (or the zone is unknown here). */
export function timeZoneShortLabel(
  timeZone: string,
  at: DateTimeInput = Date.now(),
): string {
  const ms = toMs(at) ?? Date.now();
  const part = formatter(timeZone, { timeZoneName: "short" })
    .formatToParts(ms)
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? timeZone;
}

/** `relativeTime` whose dated fallback is rendered in `timeZone`. */
export function formatRelative(value: DateTimeInput, timeZone: string): string {
  const ms = toMs(value);
  if (ms === null) return "";
  return relativeTime(ms, { formatAbsolute: (at) => formatDate(at, timeZone) });
}

export interface DateTimeFormatter {
  /** The zone every method formats in. */
  readonly timeZone: string;
  /** True once the zone is the user's stored choice rather than the detected one. */
  readonly persisted: boolean;
  /** The zone's short name for a header ("UTC", "GMT+5:30"). */
  readonly timeZoneLabel: string;
  readonly dateTime: (value: DateTimeInput) => string;
  /**
   * `dateTime`, with the caller's own word for a value that is not there.
   *
   * The absent text stays the caller's choice because it is not decoration:
   * "—" reads correctly in a definition list, and "Never" reads correctly for a
   * last-checked time where the absence IS the fact. This replaces the six
   * copies of `formatMoment` that each rendered in the browser's zone.
   */
  readonly dateTimeOr: (
    value: DateTimeInput | null | undefined,
    absent: string,
  ) => string;
  readonly date: (value: DateTimeInput) => string;
  readonly time: (value: DateTimeInput) => string;
  /** A compact instant, for a sentence that names two of them. */
  readonly dateTimeShort: (value: DateTimeInput) => string;
  readonly relative: (value: DateTimeInput) => string;
  /** The calendar date as `YYYY-MM-DD` — the KEY form, for grouping and
      comparing. `date` is the readable form of the same day. */
  readonly isoDate: (value: DateTimeInput) => string;
  /** The instant `YYYY-MM-DD` begins in this zone; null for a bad day. */
  readonly startOfDay: (day: string) => number | null;
}

export function useDateTimeFormatter(): DateTimeFormatter {
  const preferences = usePresentationPreferencesQuery();
  const timeZone = effectiveTimeZone(preferences.data);
  const persisted = (preferences.data?.version ?? 0) >= 1;
  return useMemo(
    () => ({
      timeZone,
      persisted,
      timeZoneLabel: timeZoneShortLabel(timeZone),
      dateTime: (value) => formatDateTime(value, timeZone),
      dateTimeOr: (value, absent) => formatDateTimeOr(value, timeZone, absent),
      date: (value) => formatDate(value, timeZone),
      time: (value) => formatTime(value, timeZone),
      dateTimeShort: (value) => formatDateTimeShort(value, timeZone),
      relative: (value) => formatRelative(value, timeZone),
      isoDate: (value) => isoDateInZone(value, timeZone),
      startOfDay: (day) => startOfDayInZone(day, timeZone),
    }),
    [timeZone, persisted],
  );
}
